import moment from 'moment';

export const GUEST_EVENT_QUEUE_KEY = 'guest_event_queue';
export const MAX_GUEST_EVENTS = 100;
export const GUEST_EVENT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export interface GuestEvent {
  event_id: string;
  _guest_queued_at: number;
  created_at: string;
  temp_user_id?: string;
  [key: string]: any;
}

/**
 * Safely retrieves all unexpired guest events from localStorage.
 */
export const getGuestEventQueue = (): GuestEvent[] => {
  if (typeof window === 'undefined') return [];

  try {
    const rawData = localStorage.getItem(GUEST_EVENT_QUEUE_KEY);
    if (!rawData) return [];

    const parsed: GuestEvent[] = JSON.parse(rawData);
    if (!Array.isArray(parsed)) return [];

    const now = Date.now();
    // Filter out events older than the TTL
    const activeEvents = parsed.filter(
      (item) => item?._guest_queued_at && now - item._guest_queued_at < GUEST_EVENT_TTL_MS,
    );

    // If some events expired, update localStorage immediately to maintain clean storage
    if (activeEvents.length !== parsed.length) {
      localStorage.setItem(GUEST_EVENT_QUEUE_KEY, JSON.stringify(activeEvents));
    }

    return activeEvents;
  } catch (error) {
    console.error('Failed to read guest event queue:', error);
    return [];
  }
};

/**
 * Appends an unauthenticated event to the guest queue.
 * - Caps at MAX_GUEST_EVENTS using FIFO eviction.
 * - Handles storage quota errors gracefully.
 */
export const addGuestEvent = (event: any) => {
  if (typeof window === 'undefined') return;

  try {
    const currentQueue = getGuestEventQueue();

    const tempUserId =
      localStorage.getItem('userId') || undefined;

    const guestEvent: GuestEvent = {
      ...event,
      event_id:
        event.event_id ||
        `guest_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      created_at:
        event.created_at || moment().format('YYYY-MM-DDTHH:mm:ss.SSS'),
      _guest_queued_at: Date.now(),
      ...(tempUserId ? { temp_user_id: tempUserId } : {}),
    };

    currentQueue.push(guestEvent);

    // Apply FIFO cap if queue size exceeds limit
    const trimmedQueue =
      currentQueue.length > MAX_GUEST_EVENTS
        ? currentQueue.slice(currentQueue.length - MAX_GUEST_EVENTS)
        : currentQueue;

    try {
      localStorage.setItem(
        GUEST_EVENT_QUEUE_KEY,
        JSON.stringify(trimmedQueue),
      );
    } catch (quotaError) {
      // If quota exceeded, aggressively retain only the latest 50 events
      console.warn('Storage quota reached, trimming guest event queue:', quotaError);
      const emergencyTrimmed = trimmedQueue.slice(-Math.floor(MAX_GUEST_EVENTS / 2));
      localStorage.setItem(
        GUEST_EVENT_QUEUE_KEY,
        JSON.stringify(emergencyTrimmed),
      );
    }
  } catch (error) {
    console.error('Failed to add guest event to queue:', error);
  }
};

/**
 * Atomically retrieves all pending guest events and purges them from localStorage.
 */
export const drainGuestEventQueue = (): GuestEvent[] => {
  if (typeof window === 'undefined') return [];

  try {
    const events = getGuestEventQueue();
    localStorage.removeItem(GUEST_EVENT_QUEUE_KEY);
    return events;
  } catch (error) {
    console.error('Failed to drain guest event queue:', error);
    return [];
  }
};

/**
 * Clears the guest event queue.
 */
export const clearGuestEventQueue = () => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(GUEST_EVENT_QUEUE_KEY);
  } catch (error) {
    console.error('Failed to clear guest event queue:', error);
  }
};
